import { SuiteExecutionsTable } from '@/features/executions/components/SuiteExecutionsTable';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { gridQueryKey } from '@/shared/hooks/queryKeys';
import { r, s } from '@/theme/tokens';
import { Inline } from '@/ui';
import { Box, ChevronRightIcon, IconButton, Text } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteExecutionDto, SuiteListItemDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ExecutionTablesCarouselSectionProps = {
  width: number;
  carouselIndex: number;
  direction: 'next' | 'prev';
  onPrev: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
  slides: Array<{ id: string }>;
  suiteExecutionsQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
  suitesQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
};

export const ExecutionTablesCarouselSection = ({
  width,
  carouselIndex,
  direction,
  onPrev,
  onNext,
  onSelect,
  slides,
  suiteExecutionsQuery,
  suitesQuery,
}: ExecutionTablesCarouselSectionProps): ReactElement => (
  <Box component="section" sx={{ display: { xs: 'none', sm: 'block' } }}>
    <Box sx={{ maxWidth: width, mx: 'auto', width: '100%' }}>
      <Box sx={{ mb: s('md'), textAlign: 'center' }}>
        <Text role="sectionTitle" component="h2" transform="none" mb="xs">
          Drill Down To The Detail
        </Text>
        <Text role="bodyMuted">
          Track how your suites and tests look per run, and how they evolve over time.
        </Text>
      </Box>
      <Box
        sx={{
          border: 'none',
          backgroundColor: 'transparent',
          borderRadius: r('none'),
          overflow: 'hidden',
          width: '100%',
          minWidth: 0,
          contain: 'layout paint',
          isolation: 'isolate',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `auto minmax(0, 1fr) auto`,
            alignItems: 'center',
            gap: s('sm'),
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: s('3xl') }}>
            <IconButton
              aria-label="Previous slide"
              size="large"
              onClick={onPrev}
              sx={(theme) => ({
                backgroundColor: 'transparent',
                border: 'none',
                '&:hover': { backgroundColor: 'transparent' },
                '& .MuiBox-root': {
                  width: theme.app.iconSizes.xl,
                  height: theme.app.iconSizes.xl,
                },
                '& svg': {
                  width: '100%',
                  height: '100%',
                },
              })}
            >
              <Box sx={{ transform: 'rotate(180deg)' }}>
                <ChevronRightIcon />
              </Box>
            </IconButton>
          </Box>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Box
              key={`carousel-tables-${carouselIndex}`}
              sx={(theme) => ({
                px: s('lg'),
                py: s('lg'),
                display: 'grid',
                gap: s('sm'),
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                mx: 'auto',
                animation: `landing-slide-${
                  direction === 'next' ? 'left' : 'right'
                } 420ms ${theme.app.motion.easing.standard}`,
                '@keyframes landing-slide-left': {
                  from: { opacity: 0, transform: `translateX(${s('lg')})` },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
                '@keyframes landing-slide-right': {
                  from: { opacity: 0, transform: `translateX(-${s('lg')})` },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
                '& .MuiDataGrid-root': {
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                },
              })}
            >
              {carouselIndex === 0 ? (
                <SuitesTable
                  queryKey={gridQueryKey('landing/suites')}
                  queryFn={suitesQuery}
                  pageSizeOptions={[5]}
                  initialPageSize={5}
                  disablePageSizeSelect
                  hideFooter
                  disableColumnSorting
                  persistKey={null}
                  syncStateToUrl={false}
                  showLastUpdated={false}
                  showDeltas
                  tokenOverride="local"
                  disableLinks
                />
              ) : (
                <SuiteExecutionsTable
                  queryKey={gridQueryKey('landing/suite-executions')}
                  queryFn={suiteExecutionsQuery}
                  pageSizeOptions={[5]}
                  initialPageSize={5}
                  disablePageSizeSelect
                  hideFooter
                  disableColumnSorting
                  persistKey={null}
                  syncStateToUrl={false}
                  showLastUpdated={false}
                  showExecutionRunColumn={false}
                  tokenOverride="local"
                  disableLinks
                  containerSx={{
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                    '& .MuiDataGrid-root': { width: '100%', minWidth: 0 },
                  }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: s('3xl') }}>
            <IconButton
              aria-label="Next slide"
              size="large"
              onClick={onNext}
              sx={(theme) => ({
                backgroundColor: 'transparent',
                border: 'none',
                '&:hover': { backgroundColor: 'transparent' },
                '& .MuiBox-root': {
                  width: theme.app.iconSizes.xl,
                  height: theme.app.iconSizes.xl,
                },
                '& svg': {
                  width: '100%',
                  height: '100%',
                },
              })}
            >
              <Box>
                <ChevronRightIcon />
              </Box>
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
    <Box
      sx={{
        mt: s('lg'),
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: width,
        mx: 'auto',
      }}
    >
      <Inline gap="md" align="center" sx={{ width: '100%', justifyContent: 'center' }}>
        {slides.map((slide, index) => (
          <Box
            key={`${slide.id}-tables`}
            component="button"
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => {
              onSelect(index);
            }}
            sx={(theme) => ({
              width: s('md'),
              height: s('md'),
              borderRadius: r('md'),
              border: 'none',
              padding: s('none'),
              cursor: 'pointer',
              backgroundColor:
                index === carouselIndex ? theme.app.colors.text : theme.app.colors.divider,
            })}
          />
        ))}
      </Inline>
    </Box>
  </Box>
);
